import {useCallback, useMemo, useEffect} from 'react';

import TalentSurveyQuestionsSerializer from '../../../features/talent/serializers/TalentSurveyQuestionsSerializer';
import {TalentService} from '../../../features/talent/services/TalentService';
import {TalentSurvey} from '../../../features/talent/types';
import {useGetRequest} from '../../../hooks/useGetRequest';
import {createFromChatSession} from '../../../lib/api/v1/talent/survey/createFromChatSession';
import {fetchTalentSurveyData} from '../../../lib/api/v1/talent/survey/fetchTalentSurveyData';

export type TalentSurveyFlow = 'classic' | 'chat';

export interface UseTalentSurveyPageOptions {
  flow?: TalentSurveyFlow;
  /** Get-results response (as is) for chat flow. Required when flow is 'chat'. */
  chatGetResultsPayload?: Record<string, unknown>;
}

export const useTalentSurveyPage = (
  surveyId: string,
  options?: UseTalentSurveyPageOptions
) => {
  const flow = options?.flow ?? 'classic';
  const chatGetResultsPayload = options?.chatGetResultsPayload;
  const fetchTalentSurvey = useMemo(() => {
    if (flow === 'chat') {
      if (!chatGetResultsPayload) {
        return () =>
          Promise.resolve(
            new Response(
              JSON.stringify({error_code: 'MISSING_PAYLOAD', error_message: 'Missing chat get-results payload'}),
              {status: 400}
            )
          );
      }
      return () => createFromChatSession(chatGetResultsPayload);
    }
    return () => fetchTalentSurveyData(surveyId);
  }, [surveyId, flow, chatGetResultsPayload]);

  const serializeSurvey = useCallback((data: any) => {
    return {
      applicantId: data.talent_survey.applicant_id,
      id: data.talent_survey.resource_id,
      startedAt: data.talent_survey.started_at,
      completedAt: data.talent_survey.completed_at,
      questions: TalentSurveyQuestionsSerializer.toObject(data.talent_survey),
      answers: [],
      transmitted: false,
    };
  }, []);

  const {data, loading, error} = useGetRequest<TalentSurvey>(fetchTalentSurvey, serializeSurvey);

  useEffect(() => {
    if (data) {
      if (!data.startedAt) {
        const startedSurvey = {
          ...data,
          startedAt: new Date().toISOString(),
        };
        TalentService.startTalentSurvey(startedSurvey);
      }
    }
  }, [data]);

  return {
    data: data,
    loading,
    error,
  };
};
